import { Component, OnInit, AfterViewInit, OnDestroy, Inject, PLATFORM_ID, ViewChild, ElementRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AuthService, UserSummary } from '../../core/auth/auth.service';
import { ThemeService, Theme } from '../../core/services/theme.service';
import {TESTIMONIALS} from "./constants/testimonials";
import {CONTENT_CATEGORIES} from "./constants/content-categories";
import {STAFF_MEMBERS} from "./constants/staff-members";
import {QUICK_STATS} from "./constants/quick-stats";
import {ACHIEVEMENTS} from "./constants/achievements";
import {PLATFORM_FEATURES} from "./constants/platform-features";
import {USER_SEGMENTS} from "./constants/user-segments";
import {ABOUT_HIGHLIGHTS} from "./constants/about-highlights";
import {EDUCATIONAL_OFFERS} from "./constants/educational-offers";
import {EDUCATIONAL_SERVICES} from "./constants/educational-services";
@Component({
  selector: 'app-home',
  templateUrl:'./home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  me: UserSummary | null = null;
  loading = false;
  error: string | null = null;

  // Theme dropdown properties
  isDropdownOpen = false;
  availableThemes: Theme[] = [];
  currentTheme!: Theme;
  isScrolled = false;

  // Mobile menu state
  isMenuOpen = false;

  // Add these properties to your component class
  @ViewChild('videoElement') videoElement: ElementRef<HTMLVideoElement> | undefined ;
  @ViewChild('videoSection') videoSection: ElementRef | undefined ;
  isPlaying = false;
  videoPath = 'videos/teaser.mp4'; // Local video path (from public folder)
  private videoSectionObserver: IntersectionObserver | null = null;

  // Testimonials carousel properties
  currentTestimonialIndex = 0;
  private testimonialInterval: any;

  // Modern carousel properties
  isTransitioning = false;
  isAutoplayPaused = false;
  autoplayDuration = 5000;
  cardWidth = 280; // Width of each testimonial card in pixels
  cardsVisible = 4; // Number of cards visible at once (will be responsive)

  quickStats = QUICK_STATS;
  achievements = ACHIEVEMENTS;
  platformFeatures = PLATFORM_FEATURES;
  userSegments = USER_SEGMENTS;
  aboutHighlights = ABOUT_HIGHLIGHTS;
  educationalOffers = EDUCATIONAL_OFFERS;
  educationalServices = EDUCATIONAL_SERVICES;
  staffMembers = STAFF_MEMBERS;
  contentCategories = CONTENT_CATEGORIES;
  testimonials = TESTIMONIALS;

  constructor(
    private auth: AuthService,
    public themeService: ThemeService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {

    // Initialize theme properties
    this.availableThemes = this.themeService.getAvailableThemes();
    this.currentTheme = this.themeService.getCurrentTheme();

    // Subscribe to theme changes
    this.themeService.currentTheme$.subscribe(theme => {
      this.currentTheme = theme;
    });

    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('scroll', this.onScroll.bind(this));
    }

    console.log('✅ HomeComponent ngOnInit completed');
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.setupVideoIntersectionObserver();
      this.startTestimonialAutoplay();
    }
  }

  ngOnDestroy(): void {
    if (this.videoSectionObserver) {
      this.videoSectionObserver.disconnect();
    }
    this.stopTestimonialAutoplay();
  }

  private setupVideoIntersectionObserver(): void {
    if (!this.videoSection?.nativeElement) return;

    this.videoSectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.3 && !this.isPlaying) {
            // Auto-play video when section is 30% visible
            console.log('Video section is visible, attempting autoplay');
            this.playMainVideo();
          } else if (!entry.isIntersecting && this.isPlaying) {
            // Pause video when section is not visible
            this.closeVideo();
          }
        });
      },
      {
        threshold: [0.3], // Trigger when 30% of the section is visible
        rootMargin: '0px 0px -50px 0px' // Small margin to ensure good visibility
      }
    );

    this.videoSectionObserver.observe(this.videoSection.nativeElement);
  }

  onScroll(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.isScrolled = window.scrollY > 50;
    }
  }

  smoothScroll(sectionId: string, event?: Event): void {
    console.log('🎯 smoothScroll called for:', sectionId);

    if (event) {
      event.preventDefault();
    }

    // Simply navigate to the hashtag - let CSS handle the smooth scrolling
    window.location.hash = sectionId;
  }

  playVideo(): void {
    console.log('Playing hero demo video...');
  }

  playMainVideo(): void {
    this.isPlaying = true;
    // Auto-play the video after the view updates
    setTimeout(() => {
      if (this.videoElement?.nativeElement) {
        // First, try to mute the video to allow autoplay
        this.videoElement.nativeElement.muted = true;
        this.videoElement.nativeElement.play().catch(error => {
          console.log('Video autoplay failed:', error);
          // If autoplay fails, keep the video ready but don't play
          this.isPlaying = true;
        });
      }
    }, 100);
  }

  closeVideo(): void {
    // Pause and reset the video
    if (this.videoElement?.nativeElement) {
      this.videoElement.nativeElement.pause();
      this.videoElement.nativeElement.currentTime = 0;
    }
    this.isPlaying = false;
  }

  getInitials(name: string): string {
    return name.split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  // Testimonials carousel methods with infinite scroll
  nextTestimonial(): void {
    if (this.isTransitioning) return;

    this.isTransitioning = true;
    this.currentTestimonialIndex = (this.currentTestimonialIndex + 1) % this.testimonials.length;

    // Reset transition flag after animation completes
    setTimeout(() => {
      this.isTransitioning = false;
    }, 600);
  }

  prevTestimonial(): void {
    if (this.isTransitioning) return;

    this.isTransitioning = true;
    this.currentTestimonialIndex = this.currentTestimonialIndex === 0
      ? this.testimonials.length - 1
      : this.currentTestimonialIndex - 1;

    // Reset transition flag after animation completes
    setTimeout(() => {
      this.isTransitioning = false;
    }, 600);
  }

  goToTestimonial(index: number): void {
    if (this.isTransitioning) return;

    this.isTransitioning = true;
    this.currentTestimonialIndex = index;

    // Reset transition flag after animation completes
    setTimeout(() => {
      this.isTransitioning = false;
    }, 600);
  }

  private startTestimonialAutoplay(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.testimonialInterval = setInterval(() => {
        this.nextTestimonial(); // Use the same infinite scroll logic as manual navigation
      }, this.autoplayDuration); // Auto-advance every 5 seconds
    }
  }

  private stopTestimonialAutoplay(): void {
    if (this.testimonialInterval) {
      clearInterval(this.testimonialInterval);
    }
  }

  // Modern carousel methods
  getTransform(): number {
    const containerWidth = window.innerWidth;
    let cardWidth = 280; // Desktop default

    // Responsive card width and count
    if (containerWidth < 768) {
      cardWidth = 300; // Mobile
      this.cardsVisible = 1;
    } else if (containerWidth < 1024) {
      cardWidth = 320; // Tablet
      this.cardsVisible = 2;
    } else {
      cardWidth = 280; // Desktop - show 4 cards
      this.cardsVisible = 4;
    }

    const cardWidthWithGap = cardWidth + 24; // 24px gap between cards
    return -(this.currentTestimonialIndex * cardWidthWithGap);
  }

  getExtendedTestimonials() {
    // Create endless effect by duplicating testimonials
    const extended = [...this.testimonials, ...this.testimonials, ...this.testimonials];
    return extended;
  }

  getCardTransform(index: number): string {
    // Add subtle 3D perspective effect on hover (handled by CSS :hover)
    return 'translateZ(0)';
  }

  pauseAutoplay(): void {
    this.isAutoplayPaused = true;
    this.stopTestimonialAutoplay();
  }

  resumeAutoplay(): void {
    this.isAutoplayPaused = false;
    this.startTestimonialAutoplay();
  }

  getStarArray(rating: number): number[] {
    return Array(rating).fill(0);
  }

  getEmptyStarArray(rating: number): number[] {
    return Array(5 - rating).fill(0);
  }

  getAvatarColor(index: number): string {
    const colors = [
      'avatar-blue', 'avatar-purple', 'avatar-pink', 'avatar-green',
      'avatar-orange', 'avatar-teal', 'avatar-red', 'avatar-indigo',
      'avatar-yellow', 'avatar-emerald'
    ];
    return colors[index % colors.length];
  }

  getCurrentDotIndex(): number {
    return this.currentTestimonialIndex % this.testimonials.length;
  }

  private animateStats(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    setTimeout(() => {
      const statNumbers = document.querySelectorAll('.stat-number');
      statNumbers.forEach((element: any) => {
        const target = parseInt(element.getAttribute('data-target'));
        if (!target) return;

        let current = 0;
        const increment = target / 50;
        const timer = setInterval(() => {
          current += increment;
          if (current >= target) {
            current = target;
            clearInterval(timer);
          }

          if (element.textContent?.includes('%')) {
            element.textContent = Math.floor(current) + '%';
          } else if (element.textContent?.includes('+')) {
            element.textContent = Math.floor(current).toLocaleString() + '+';
          } else {
            element.textContent = Math.floor(current).toLocaleString();
          }
        }, 30);
      });
    }, 1000);
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  selectTheme(theme: Theme): void {
    this.themeService.setTheme(theme);
    this.isDropdownOpen = false;
  }

  // Hamburger menu method
  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
    console.log('Menu is now:', this.isMenuOpen ? 'OPEN' : 'CLOSED');
  }

  getThemeGradient(): string {
    const theme = this.themeService.getCurrentTheme();
    return `linear-gradient(135deg, ${theme.colors.start} 0%, ${theme.colors.middle} 50%, ${theme.colors.end} 100%)`;
  }

  getThemeGradientForTheme(theme: Theme): string {
    return `linear-gradient(135deg, ${theme.colors.start} 0%, ${theme.colors.middle} 50%, ${theme.colors.end} 100%)`;
  }
}
